const env = (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env;
const supabaseUrl = env.VITE_SUPABASE_URL ?? env.SUPABASE_URL;

/**
 * Resolves a media path or URL against the configured Supabase host.
 *
 * Handles:
 * - JSON-wrapped strings: `["https://..."]` or `"https://..."`
 * - Full URLs from other IPs/ports (re-anchors to current VITE_SUPABASE_URL if storage endpoint)
 * - Encoded query parameters (`%3Ftoken=`)
 * - Relative paths with or without `media/` bucket prefix
 * - Double bucket prefix prevention (`/media/media/...`)
 * - Browser-local URLs (`blob:`, `data:`)
 */
export function resolveMediaUrl(rawUrl: string, baseUrl = supabaseUrl): string {
  if (!rawUrl) return '';

  let url = rawUrl.trim();

  // 1. Unwrap JSON string / quotes if passed
  if (
    (url.startsWith('[') && url.endsWith(']')) ||
    (url.startsWith('"') && url.endsWith('"')) ||
    (url.startsWith("'") && url.endsWith("'"))
  ) {
    try {
      const parsed = JSON.parse(url);
      if (Array.isArray(parsed) && parsed.length > 0) {
        url = String(parsed[0]).trim();
      } else if (typeof parsed === 'string') {
        url = parsed.trim();
      }
    } catch {
      url = url.replace(/^[\["']+|[\]"']+$/g, '').trim();
    }
  }

  if (!url) return '';

  // 2. Browser local URLs or protocol-relative URLs
  if (url.startsWith('blob:') || url.startsWith('data:') || url.startsWith('//')) {
    return url;
  }

  // 3. Normalize encoded token
  url = url.replaceAll('%3Ftoken=', '?token=');

  // 4. If absolute HTTP(S) URL
  if (/^https?:\/\//i.test(url)) {
    if (baseUrl) {
      try {
        const parsedUrl = new URL(url);
        const parsedBase = new URL(baseUrl);

        // If it points to a Supabase storage path, ensure it uses the active baseUrl host/port/scheme
        if (parsedUrl.pathname.includes('/storage/v1/')) {
          const needsRebase =
            parsedUrl.hostname === 'localhost' ||
            parsedUrl.hostname === '127.0.0.1' ||
            parsedUrl.port === '54321' ||
            parsedUrl.hostname !== parsedBase.hostname ||
            parsedUrl.protocol !== parsedBase.protocol;

          if (needsRebase) {
            parsedUrl.protocol = parsedBase.protocol;
            parsedUrl.hostname = parsedBase.hostname;
            parsedUrl.port = parsedBase.port;
            return parsedUrl.toString();
          }
        }
      } catch {
        // Fallback to original url
      }
    }
    return url;
  }

  // 5. If relative path
  if (!baseUrl) return url;

  try {
    const path = url.startsWith('/') ? url : `/${url}`;

    // If already has /storage/v1/ prefix
    if (path.startsWith('/storage/v1/')) {
      return new URL(path, baseUrl).toString();
    }

    // If already has /media/ prefix
    if (path.startsWith('/media/')) {
      const storagePath = `/storage/v1/object/sign${path}`;
      return new URL(storagePath, baseUrl).toString();
    }

    // Bare path without bucket prefix: mc-group/project/photo.jpg?token=...
    const storagePath = `/storage/v1/object/sign/media${path}`;
    return new URL(storagePath, baseUrl).toString();
  } catch {
    return url;
  }
}

